import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OverlayPage } from "@/components/layout/OverlayPage";
import { FeatureComparisonCard } from "@/components/onboarding/FeatureComparisonCard";
import { useOnboarding } from "@/hooks/useOnboarding";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SmartOrderInput } from "@/components/orders/SmartOrderInput";
import { ManualOrderForm } from "@/components/orders/ManualOrderForm";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useInvalidateOrderQueries } from "@/hooks/useInvalidateOrderQueries";
import { useOverlayState } from "@/hooks/useOverlayState";

// Lazy load confetti for performance
const triggerConfetti = async () => {
  const confetti = (await import("canvas-confetti")).default;

  // OrderFlow brand colors: purple-blue gradient + success/warning accents
  confetti({
    particleCount: 150,
    spread: 100,
    origin: { y: 0.6 },
    colors: ["#667eea", "#764ba2", "#10b981", "#fbbf24"],
    ticks: 250,
  });

  // Second burst for extra celebration
  setTimeout(() => {
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.7, x: 0.3 },
      colors: ["#667eea", "#764ba2"],
    });
  }, 200);

  setTimeout(() => {
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.7, x: 0.7 },
      colors: ["#10b981", "#fbbf24"],
    });
  }, 400);
};

// Persist last used mode
const STORAGE_KEY = "orderflow:neworder:mode";

export default function NewOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") === "smart" ? "smart" : "manual";

  // Load persisted mode or use URL param
  const [activeMode, setActiveMode] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "smart" || stored === "manual") return stored;
    }
    return defaultTab;
  });

  const { t } = useLanguage();
  const {
    state: onboardingState,
    markFeatureComparisonSeen,
    markFirstOrderCreated,
  } = useOnboarding();
  const overlay = useOverlayState();
  const [showComparison, setShowComparison] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const isMobile = useIsMobile();
  const { invalidateAll } = useInvalidateOrderQueries();
  
  // Smart Input exposes its primary action dynamically (Process AI vs Save)
  const [smartPrimaryAction, setSmartPrimaryAction] = useState<{
    label: string;
    onClick: () => void;
    disabled: boolean;
  } | null>(null);

  // Show comparison on desktop for first-time users
  useEffect(() => {
    if (!onboardingState.hasSeenFeatureComparison && !isMobile) {
      setShowComparison(true);
    }
  }, [onboardingState.hasSeenFeatureComparison, isMobile]);

  // Persist mode changes
  const handleModeChange = useCallback((mode: string) => {
    setActiveMode(mode);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  }, []);

  const handleDismissComparison = () => {
    setShowComparison(false);
    markFeatureComparisonSeen();
  };

  const toggleComparison = () => {
    setShowComparison(!showComparison);
    if (!onboardingState.hasSeenFeatureComparison) {
      markFeatureComparisonSeen();
    }
  };

  const handleOrderCreated = useCallback(() => {
    // Mark as submitted (allows immediate close without confirmation)
    overlay.complete();
    
    // Celebrate first order
    if (!onboardingState.hasCreatedFirstOrder) {
      markFirstOrderCreated();
      triggerConfetti();
    }

    invalidateAll();
    navigate("/orders");
  }, [
    onboardingState.hasCreatedFirstOrder,
    markFirstOrderCreated,
    invalidateAll,
    navigate,
    overlay,
  ]);

  const handleClose = useCallback(() => {
    navigate("/orders");
  }, [navigate]);

  // Track dirty state when form content changes
  const handleDirtyChange = useCallback((dirty: boolean) => {
    overlay.setDirty(dirty);
  }, [overlay]);

  const breadcrumbs = [
    { label: t("nav.orders"), href: "/orders" },
    { label: t("orders.addOrder") },
  ];

  const modeOptions = [
    { value: "smart", label: t("orders.add.smartInput") },
    { value: "manual", label: t("orders.add.manualEntry") },
  ];

  return (
    <OverlayPage
      title={t("orders.addOrder")}
      breadcrumbs={breadcrumbs}
      isDirty={overlay.isDirty}
      isProcessing={overlay.state === "processing"}
      overlayState={overlay}
      onClose={handleClose}
      primaryAction={
        activeMode === "smart" && smartPrimaryAction
          ? {
              label: smartPrimaryAction.label,
              onClick: smartPrimaryAction.onClick,
              loading: formLoading,
              disabled: smartPrimaryAction.disabled || formLoading,
            }
          : {
              label: t("common.save"),
              onClick: () => {
                // Submit will be handled by the active form
                const form = document.querySelector("form");
                if (form) {
                  form.requestSubmit();
                }
              },
              loading: formLoading,
              disabled: formLoading,
            }
      }
      secondaryAction={{
        label: t("common.cancel"),
        onClick: handleClose,
      }}
    >
      <div className="space-y-4">
        {/* Feature Comparison Card - Only on desktop */}
        {showComparison && !isMobile && (
          <FeatureComparisonCard onDismiss={handleDismissComparison} />
        )}

        {/* Mode selector + Help toggle row */}
        <div className="flex items-center justify-between gap-2">
          {/* Segmented control for mode selection */}
          <SegmentedControl
            value={activeMode}
            onValueChange={handleModeChange}
            options={modeOptions}
          />

          {/* Help toggle - Only on desktop */}
          {!isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleComparison}
              className="h-8 w-8 p-0"
              title={t("orders.add.toggleHelp") || "Toggle comparison help"}
            >
              <HelpCircle
                className={`h-4 w-4 ${showComparison ? "text-primary" : "text-muted-foreground"}`}
              />
            </Button>
          )}
        </div>

        {/* Content - Conditional rendering for better performance */}
        <div>
          {activeMode === "smart" && (
            <SmartOrderInput
              onOrderCreated={handleOrderCreated}
              onCancel={handleClose}
              onDirtyChange={handleDirtyChange}
              onLoadingChange={setFormLoading}
              onPrimaryActionChange={setSmartPrimaryAction}
            />
          )}
          {activeMode === "manual" && (
            <ManualOrderForm
              onOrderCreated={handleOrderCreated}
              onCancel={handleClose}
              onDirtyChange={handleDirtyChange}
              onLoadingChange={setFormLoading}
            />
          )}
        </div>
      </div>
    </OverlayPage>
  );
}
